import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const Hero = () => {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const scrimRef = useRef(null);
  const logoRef = useRef(null);
  const titleRef = useRef(null);
  const ruleRef = useRef(null);
  const sinceRef = useRef(null);
  const hintRef = useRef(null);
  const tintRef = useRef(null);
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.set(logoRef.current, { opacity: 0, scale: 0.55, y: 70 });
        gsap.set(titleRef.current, {
          opacity: 0,
          y: 46,
          clipPath: "inset(0% 0% 100% 0%)",
        });
        gsap.set(ruleRef.current, { scaleX: 0 });
        gsap.set(sinceRef.current, { opacity: 0, y: 16 });
        gsap.set(scrimRef.current, { opacity: 0 });

        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "+=160%",
            scrub: 1,
            pin: true,
            anticipatePin: 1,
          },
        });

        tl.to(bgRef.current, { scale: 1.12, yPercent: 5, duration: 1.6 }, 0)
          .to(hintRef.current, { opacity: 0, duration: 0.15 }, 0)
          .to(tintRef.current, { opacity: 0, duration: 0.7 }, 0.1)
          .to(scrimRef.current, { opacity: 1, duration: 0.55 }, 0.05)
          .to(
            logoRef.current,
            { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: "power2.out" },
            0.12
          )
          .to(
            titleRef.current,
            {
              opacity: 1,
              y: 0,
              clipPath: "inset(0% 0% 0% 0%)",
              duration: 0.42,
              ease: "power1.out",
            },
            0.62
          )
          .to(ruleRef.current, { scaleX: 1, duration: 0.28 }, 1.0)
          .to(sinceRef.current, { opacity: 1, y: 0, duration: 0.34 }, 1.05)
          .to(
            contentRef.current,
            { opacity: 0, y: -56, duration: 0.35, ease: "power1.in" },
            1.45
          )
          .to({}, { duration: 0.2 });
      });

      mm.add("(prefers-reduced-motion: reduce)", () => {
        gsap.set(scrimRef.current, { opacity: 1 });
        gsap.set(tintRef.current, { opacity: 0 });
        gsap.set(hintRef.current, { opacity: 0 });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative h-[100dvh] overflow-hidden"
      data-testid="hero-section"
      aria-label="Val-Hue campus establishing shot"
    >
      <div ref={bgRef} className="hero-mask absolute inset-0 will-change-transform">
        <div
          aria-hidden="true"
          className="absolute inset-0 md:hidden"
          style={{
            background:
              "radial-gradient(ellipse 95% 62% at 50% 50%, #143A66 0%, #0A2647 72%)",
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center md:block">
          <img
            src="/campus-hero.jpg"
            alt="Val-Hue High School and Junior College campus building"
            className="hero-img hero-img-photo w-full md:h-full md:object-cover md:object-[34%_70%] md:drop-shadow-[0_20px_50px_rgba(4,18,36,0.5)]"
            fetchPriority="high"
          />
        </div>
        <div className="hero-grade absolute inset-0" aria-hidden="true" />
        <div
          ref={tintRef}
          className="absolute inset-0 md:hidden"
          style={{ background: "rgba(10, 38, 71, 0.55)" }}
          aria-hidden="true"
        />
      </div>

      <div ref={scrimRef} className="hero-scrim absolute inset-0" aria-hidden="true" />

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center" ref={contentRef}>
        <span
          ref={logoRef}
          className="block h-32 w-32 overflow-hidden rounded-full shadow-[0_24px_60px_-12px_rgba(4,18,36,0.65)] ring-1 ring-white/40 will-change-transform md:h-44 md:w-44"
        >
          <img
            src="/logo.png"
            alt="Val-Hue High School emblem"
            data-testid="hero-logo"
            className="h-full w-full scale-[2.9] object-cover object-[49%_49%]"
          />
        </span>

        <h1
          ref={titleRef}
          data-testid="hero-title"
          className="mt-8 max-w-4xl font-display text-3xl font-semibold leading-tight tracking-[0.05em] text-white will-change-transform sm:text-4xl md:mt-10 md:text-5xl lg:text-6xl"
        >
          VAL-HUE HIGH SCHOOL <span className="text-[#F47221]">&amp;</span> JUNIOR
          COLLEGE
        </h1>

        <span
          ref={ruleRef}
          className="mt-6 block h-[2px] w-20 origin-center bg-[#F47221] md:w-28"
          aria-hidden="true"
        />

        <p
          ref={sinceRef}
          data-testid="hero-since"
          className="mt-5 text-xs font-semibold uppercase tracking-[0.5em] text-[#D6EBEE] md:text-sm"
        >
          Since 2002
        </p>
      </div>

      <div
        ref={hintRef}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        aria-hidden="true"
      >
        <div className="flex flex-col items-center gap-3 text-white/70">
          <span className="text-[10px] font-medium uppercase tracking-[0.4em]">
            Scroll
          </span>
          <span className="scroll-line block h-10 w-px bg-white/50" />
        </div>
      </div>
    </section>
  );
};
